// Drop-in replacement for the SDK's CameraPermissionGate. The original
// ships "Open Settings" + "Retry" buttons but no close — visitors who
// tap Photo/Barcode on web get stuck (camera permissions can't be
// granted via web in any useful way). This shim adds an X top-right
// that dispatches a `miri-close-meal-hub` event on `window`, which
// App.tsx listens for and closes the ScanFoodModal.
//
// Routed via the waterGlassSvgShim-style resolveId plugin in
// vite.config.mts.

import { FC, ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

interface CameraPermissionGateProps {
  children?: ReactNode;
  permissionText?: string;
  testID?: string;
}

export const CameraPermissionGate: FC<CameraPermissionGateProps> = ({
  permissionText = 'Camera access is needed to scan barcodes or take photos of meals.',
}) => {
  // `onPress` alone doesn't fire reliably for an RN-Web Pressable — it
  // needs a real DOM `onClick` plus `cursor: pointer` (the same wiring
  // every interactive Pressable in App.tsx uses). Without it the X
  // looks tappable but does nothing on web.
  const close = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('miri-close-meal-hub'));
    }
  };
  return (
    <View style={styles.root}>
      <Pressable
        onPress={close}
        onClick={close}
        style={[styles.closeBtn, { cursor: 'pointer' }]}
        accessibilityRole="button"
        accessibilityLabel="Close"
      >
        <Text style={styles.closeLabel}>✕</Text>
      </Pressable>
      <Text style={styles.header}>Photo & barcode scanning are mobile-only</Text>
      <Text style={styles.body}>
        {permissionText}
      </Text>
      <Text style={styles.hint}>
        Open this demo on your phone, or use Search to log a meal by name.
      </Text>
    </View>
  );
};

export default CameraPermissionGate;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  closeLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1B1B16',
  },
  header: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1B1B16',
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    color: '#5F584F',
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 320,
  },
  hint: {
    fontSize: 12,
    color: '#928A7E',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
});
